import {useState} from 'react';
import {useMutation} from '@apollo/client';
import {Alert, Avatar, Button, Input, List, Tag, Typography} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import {personName, unwrap} from 'pages/kybDashboard/kybUtils';
import {
  ADD_KYC_HUB_NOTE,
  DELETE_KYC_HUB_NOTE,
  KYC_HUB_NOTES,
  UPDATE_KYC_HUB_NOTE,
} from './resolvers';
import {HUB_PAGE_SIZE} from './kycHubUtils';
import {useHubList} from './useHubList';
import {ReasonModal} from './ReasonModal';
import {TogglePill} from './TogglePill';
import styles from './kycHub.module.scss';

const {Text} = Typography;

const REFETCH = ['KycHubNotes', 'KycHubActivity'];
const MAX_NOTE = 5000;
const stamp = value => moment(value).format('DD MMM YYYY, HH:mm');
const initials = name =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0].toUpperCase())
    .join('') || '?';

/**
 * Internal notes: attributed, timestamped, never shown to the customer.
 *
 * Edits and archives keep the record — the timeline holds the previous text —
 * so an archived note is read-only and only visible with history switched on.
 */
export const HubNotes = ({subject, canUpdate}) => {
  const list = useHubList(KYC_HUB_NOTES, 'kycHubNotes', subject);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null); // {id, text, original}
  const [archiveTarget, setArchiveTarget] = useState(null);

  const [addNote, {loading: adding}] = useMutation(ADD_KYC_HUB_NOTE);
  const [updateNote, {loading: saving}] = useMutation(UPDATE_KYC_HUB_NOTE);
  const [deleteNote, {loading: archiving}] = useMutation(DELETE_KYC_HUB_NOTE);

  const run = async (mutate, field, input, success) => {
    try {
      const {data} = await mutate({
        variables: {input},
        refetchQueries: REFETCH,
        awaitRefetchQueries: true,
      });
      const {error} = unwrap(data?.[field]);
      if (error) {
        toast.error(error);
        return false;
      }
      toast.success(success);
      return true;
    } catch (error) {
      toast.error('The request failed. Try again.');
      return false;
    }
  };

  const handleAdd = async () => {
    const text = draft.trim();
    if (!text) return;
    if (
      await run(addNote, 'addKycHubNote', {...subject, text}, 'Note added.')
    ) {
      setDraft('');
    }
  };

  const handleSave = async () => {
    const text = editing.text.trim();
    const done = await run(
      updateNote,
      'updateKycHubNote',
      {noteId: editing.id, text},
      'Note updated.',
    );
    if (done) setEditing(null);
  };

  const handleArchive = async reason => {
    const done = await run(
      deleteNote,
      'deleteKycHubNote',
      {noteId: archiveTarget.id, reason},
      'Note archived.',
    );
    if (done) setArchiveTarget(null);
  };

  const editUnchanged =
    editing && editing.text.trim() === editing.original.trim();

  return (
    <div>
      {canUpdate && (
        <div className={styles.noteComposer}>
          <Input.TextArea
            autoSize={{minRows: 3, maxRows: 10}}
            value={draft}
            maxLength={MAX_NOTE}
            placeholder="Write a note for other admins…"
            onChange={event => setDraft(event.target.value)}
          />
          <div className={styles.noteComposerFooter}>
            <span className={styles.subtle}>
              Only admins see internal notes. {draft.length} / {MAX_NOTE}
            </span>
            <Button
              type="primary"
              loading={adding}
              disabled={!draft.trim() || adding}
              onClick={handleAdd}>
              Add note
            </Button>
          </div>
        </div>
      )}

      <div className={styles.noteListHead}>
        <span className={styles.noteCount}>
          {list.total} note{list.total === 1 ? '' : 's'}
        </span>
        <TogglePill
          active={Boolean(list.filters.includeDeleted)}
          onChange={includeDeleted => list.setFilter({includeDeleted})}>
          Show archived notes
        </TogglePill>
      </div>

      {list.error && (
        <Alert
          type="error"
          showIcon
          className={styles.alert}
          message={list.error}
        />
      )}

      <List
        loading={list.loading}
        dataSource={list.rows}
        split={false}
        locale={{
          emptyText: (
            <div className={styles.noteEmpty}>
              <strong>No notes yet</strong>
              <span>
                {canUpdate
                  ? 'Notes you add above show here.'
                  : 'Admins’ notes about this verification show here.'}
              </span>
            </div>
          ),
        }}
        // One page needs no pager.
        pagination={
          list.total > HUB_PAGE_SIZE && {
            current: list.page,
            pageSize: HUB_PAGE_SIZE,
            total: list.total,
            onChange: list.setPage,
            showSizeChanger: false,
          }
        }
        renderItem={note => {
          const archived = Boolean(note.deletedAt);
          const isEditing = editing?.id === note.id;
          return (
            <List.Item
              className={`${styles.noteItem} ${
                archived ? styles.archivedNote : ''
              }`.trim()}
              actions={
                canUpdate && !archived && !isEditing
                  ? [
                      <Button
                        key="edit"
                        size="small"
                        onClick={() =>
                          setEditing({
                            id: note.id,
                            text: note.text,
                            original: note.text,
                          })
                        }>
                        Edit
                      </Button>,
                      <Button
                        key="archive"
                        size="small"
                        danger
                        onClick={() => setArchiveTarget(note)}>
                        Archive
                      </Button>,
                    ]
                  : []
              }>
              <Avatar size={32} className={styles.noteAvatar}>
                {initials(personName(note.author))}
              </Avatar>
              <div className={styles.note}>
                <div className={styles.noteMeta}>
                  <strong>{personName(note.author)}</strong>
                  <span className={styles.subtle}>{stamp(note.createdAt)}</span>
                  {note.editedAt && (
                    <span className={styles.subtle}>
                      · edited {stamp(note.editedAt)}
                    </span>
                  )}
                  {archived && <Tag>Archived</Tag>}
                </div>

                {isEditing ? (
                  <div className={styles.noteEditor}>
                    <Input.TextArea
                      rows={3}
                      value={editing.text}
                      maxLength={MAX_NOTE}
                      showCount
                      onChange={event =>
                        setEditing({...editing, text: event.target.value})
                      }
                    />
                    <div className={styles.rowActions}>
                      <Button size="small" onClick={() => setEditing(null)}>
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        loading={saving}
                        disabled={!editing.text.trim() || editUnchanged}
                        onClick={handleSave}>
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Text className={styles.noteText}>{note.text}</Text>
                )}

                {archived && (
                  <span className={styles.subtle}>
                    Archived {stamp(note.deletedAt)}
                    {note.deletionReason ? ` · ${note.deletionReason}` : ''}
                  </span>
                )}
              </div>
            </List.Item>
          );
        }}
      />

      <ReasonModal
        open={Boolean(archiveTarget)}
        title="Archive note"
        description="The note leaves the list. It stays in the history, with this reason, and can no longer be edited."
        placeholder="e.g. Entered in error"
        confirmLabel="Archive note"
        submitting={archiving}
        onClose={() => setArchiveTarget(null)}
        onSubmit={handleArchive}
      />
    </div>
  );
};
